import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, MapPin, CircleDot, ArrowRight, Calendar } from "lucide-react";
import { api } from "@/lib/api";

const Coaches = () => {
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sport, setSport] = useState("");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    (async () => {
      try {
        const { data } = await api.get(`/coaches${sport ? `?sport=${sport}` : ""}`);
        setCoaches(data);
      } finally {
        setLoading(false);
      }
    })();
  }, [sport]);

  return (
    <div className="bg-spike-surface min-h-screen" data-testid="coaches-page">
      <section className="relative pt-16 pb-12 border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 stripe-pattern opacity-50" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="font-orbitron text-[0.7rem] uppercase tracking-[0.4em] text-spike-blue-light flex items-center gap-3">
            <span className="w-8 h-px bg-spike-blue" /> Coaches
          </div>
          <h1 className="mt-6 font-orbitron font-black uppercase text-4xl sm:text-5xl lg:text-6xl text-spike-text leading-[1] tracking-tight">
            Train with <span className="text-spike-blue-light">people who play.</span>
          </h1>
          <p className="mt-6 font-exo text-spike-text-muted max-w-2xl">
            Our coaches are players first. Hand-picked across Ontario for technique, temperament,
            and the ability to break the game down for any level.
          </p>

          <div className="mt-10 flex gap-px bg-white/5 w-fit" data-testid="coach-filters">
            {[
              { id: "", label: "All" },
              { id: "cricket", label: "Cricket" },
              { id: "baseball", label: "Baseball" },
            ].map((f) => (
              <button
                key={f.id || "all"}
                type="button"
                data-testid={`coach-filter-${f.id || "all"}`}
                onClick={() => setSport(f.id)}
                className={`px-5 py-3 font-orbitron text-[0.7rem] uppercase tracking-[0.25em] transition-colors ${
                  sport === f.id ? "bg-spike-blue text-white" : "bg-spike-elevated text-spike-text-muted hover:text-spike-text"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          {loading ? (
            <div className="py-20 flex justify-center text-spike-text-muted gap-3">
              <Loader2 className="w-5 h-5 animate-spin text-spike-blue-light" /> Loading coaches...
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5" data-testid="coaches-grid">
              {coaches.map((c, i) => (
                <motion.div
                  key={c.id}
                  data-testid={`coach-card-${c.id}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.07, duration: 0.5 }}
                  className="bg-spike-elevated p-0 group"
                >
                  <div className="aspect-[4/5] overflow-hidden relative">
                    <img
                      src={c.image}
                      alt={c.name}
                      className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                    <div className="absolute top-5 left-5 bg-spike-blue/90 px-3 py-1.5">
                      <span className="font-orbitron text-[0.6rem] font-bold uppercase tracking-[0.3em] text-spike-text">
                        {c.sports.join(" · ")}
                      </span>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-5">
                      <div className="font-orbitron text-[0.6rem] uppercase tracking-[0.3em] text-spike-blue-light">
                        {c.role}
                      </div>
                      <div className="mt-1 font-orbitron font-black uppercase text-2xl text-spike-text tracking-wide">
                        {c.name}
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <p className="font-exo text-sm text-spike-text-muted leading-relaxed">{c.bio}</p>
                    <ul className="mt-5 space-y-2 font-exo text-xs text-spike-text-muted">
                      <li className="flex items-center gap-2">
                        <MapPin className="w-3 h-3 text-spike-blue-light" /> {c.venues.join(" · ")}
                      </li>
                      <li className="flex items-center gap-2">
                        <Calendar className="w-3 h-3 text-spike-blue-light" /> {c.available_days.join(" / ")}
                      </li>
                      <li className="flex items-center gap-2">
                        <CircleDot className="w-3 h-3 text-spike-blue-light" />
                        <span className="font-orbitron text-spike-text tracking-wider">${c.hourly_rate_cad}/hr CAD</span>
                      </li>
                    </ul>
                    <Link
                      to={`/book?coach=${c.id}&sport=${c.sports[0]}&venue=${c.venues[0]}`}
                      data-testid={`coach-book-${c.id}`}
                      className="mt-6 inline-flex w-full items-center justify-center gap-2 bg-transparent text-spike-text border border-white/10 hover:border-spike-blue hover:bg-spike-blue/10 font-orbitron text-[0.7rem] uppercase tracking-[0.22em] py-3 transition-colors"
                    >
                      Book with {c.name.split(" ")[0]} <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Coaches;
